import React, { useState, useEffect, useRef } from 'react';
import messageController from '../../controllers/messageController';

const MessagesPage = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [filter, setFilter] = useState('all');
    const [showInfo, setShowInfo] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    
    // New conversation modal states
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isGroupChat, setIsGroupChat] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [creatingConversation, setCreatingConversation] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    const currentUserId = localStorage.getItem('userId') || 'currentUser';

    useEffect(() => {
        loadConversations();
    }, [filter, searchQuery]);

    useEffect(() => {
        if (selectedChat) {
            loadMessages(selectedChat.id);
            markConversationAsRead(selectedChat.id);
        }
    }, [selectedChat]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!selectedChat) return;
        const interval = setInterval(() => {
            pollNewMessages();
        }, 3000);
        return () => clearInterval(interval);
    }, [selectedChat, messages]);

    // Fetch all users when modal opens
    useEffect(() => {
        if (showNewChatModal) {
            fetchAllUsers();
        }
    }, [showNewChatModal]);

    const fetchAllUsers = async () => {
        setLoadingUsers(true);
        try {
            // Use messageController to get users
            const result = await messageController.getUsersList();
            
            if (result.success) {
                console.log('Fetched users:', result.data);
                
                // Filter out current user (extra safety)
                const filteredUsers = result.data.filter(
                    user => {
                        const userId = user._id || user.id;
                        return userId !== currentUserId;
                    }
                );
                
                console.log('Filtered users:', filteredUsers);
                setAllUsers(filteredUsers);
            } else {
                console.error('Failed to fetch users:', result.message);
                setAllUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setAllUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadConversations = async () => {
        setLoading(true);
        try {
            const result = await messageController.getAllConversations({
                filter,
                search: searchQuery
            });

            if (result.success) {
                const formatted = messageController.formatConversationsForUI(
                    result.data,
                    currentUserId
                );
                setConversations(formatted);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMessages = async (conversationId) => {
        try {
            const result = await messageController.getMessages(conversationId, {
                page: 1,
                limit: 50
            });

            if (result.success) {
                const formatted = messageController.formatMessagesForUI(
                    result.data,
                    currentUserId
                );
                setMessages(formatted);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
        }
    };

    const pollNewMessages = async () => {
        if (!selectedChat || messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];
        const result = await messageController.pollNewMessages(
            selectedChat.id,
            lastMessage.timestamp
        );

        if (result.success && result.data.length > 0) {
            const formatted = messageController.formatMessagesForUI(
                result.data,
                currentUserId
            );
            setMessages(prev => [...prev, ...formatted]);
        }
    };

    const sendNewMessage = async (e) => {
        e.preventDefault();
        if (!message.trim() || !selectedChat || sending) return;

        const tempMessage = {
            id: 'temp-' + Date.now(),
            text: message,
            senderId: currentUserId,
            timestamp: new Date(),
            status: 'sending',
            isMine: true,
            type: 'text'
        };

        setMessages(prev => [...prev, tempMessage]);
        setMessage('');
        setSending(true);

        try {
            const result = await messageController.sendMessage({
                conversationId: selectedChat.id,
                text: message
            });

            if (result.success) {
                setMessages(prev => prev.map(msg => 
                    msg.id === tempMessage.id ? {
                        ...messageController.formatMessagesForUI([result.data], currentUserId)[0],
                        isMine: true
                    } : msg
                ));
                await loadConversations();
            } else {
                setMessages(prev => prev.map(msg => 
                    msg.id === tempMessage.id ? { ...msg, status: 'failed' } : msg
                ));
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => prev.map(msg => 
                msg.id === tempMessage.id ? { ...msg, status: 'failed' } : msg
            ));
        } finally {
            setSending(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedChat || uploadingFile) return;

        setUploadingFile(true);

        try {
            const result = await messageController.uploadFile({
                conversationId: selectedChat.id,
                file
            });

            if (result.success) {
                const formatted = messageController.formatMessagesForUI(
                    [result.data],
                    currentUserId
                );
                setMessages(prev => [...prev, { ...formatted[0], isMine: true }]);
                await loadConversations();
            } else {
                alert('Failed to upload file: ' + result.message);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Error uploading file');
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const markConversationAsRead = async (conversationId) => {
        try {
            await messageController.markAsRead(conversationId);
            setConversations(prev => prev.map(conv => 
                conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
            ));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleTyping = (e) => {
        setMessage(e.target.value);
        if (!selectedChat) return;

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        messageController.sendTypingIndicator(selectedChat.id, true);

        typingTimeoutRef.current = setTimeout(() => {
            messageController.sendTypingIndicator(selectedChat.id, false);
        }, 2000);
    };

    const handleDeleteMessage = async (messageId) => {
        try {
            const isConfirmed = window.confirm('Are you sure you want to delete this message?');
            if (!isConfirmed) return;

            const result = await messageController.deleteMessage(messageId);

            if (result?.success) {
                setMessages(prev => prev.filter(msg => (msg.id || msg._id) !== messageId));
            } else {
                alert(result?.message || 'Failed to delete message');
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            alert('Error deleting message');
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // NEW CONVERSATION FUNCTIONS
    const openNewChatModal = () => {
        setShowNewChatModal(true);
        setSelectedUsers([]);
        setIsGroupChat(false);
        setGroupName('');
        setUserSearchQuery('');
    };

    const closeNewChatModal = () => {
        setShowNewChatModal(false);
        setSelectedUsers([]);
        setIsGroupChat(false);
        setGroupName('');
        setUserSearchQuery('');
    };

    const toggleUserSelection = (user) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(u => u._id === user._id || u.id === user.id);
            if (isSelected) {
                return prev.filter(u => (u._id || u.id) !== (user._id || user.id));
            } else {
                return [...prev, user];
            }
        });
    };

    const handleCreateConversation = async () => {
        if (selectedUsers.length === 0) {
            alert('Please select at least one user');
            return;
        }

        if (isGroupChat && !groupName.trim()) {
            alert('Please enter a group name');
            return;
        }

        if (isGroupChat && selectedUsers.length < 2) {
            alert('Group chat requires at least 2 users');
            return;
        }

        setCreatingConversation(true);

        try {
            if (isGroupChat) {
                // Create group conversation
                const result = await messageController.createConversation({
                    type: 'group',
                    name: groupName.trim(),
                    participantIds: selectedUsers.map(u => u._id || u.id)
                });

                if (result.success) {
                    await loadConversations();
                    setSelectedChat(result.data);
                    closeNewChatModal();
                } else {
                    alert('Failed to create group: ' + result.message);
                }
            } else {
                // Create direct conversation (single user)
                const user = selectedUsers[0];
                const result = await messageController.createConversation({
                    participantId: user._id || user.id,
                    type: 'direct'
                });

                if (result.success) {
                    await loadConversations();
                    setSelectedChat(result.data);
                    closeNewChatModal();
                } else {
                    alert('Failed to create conversation: ' + result.message);
                }
            }
        } catch (error) {
            console.error('Error creating conversation:', error);
            alert('Error creating conversation');
        } finally {
            setCreatingConversation(false);
        }
    };

    // Filter users based on search
    const filteredUsers = allUsers.filter(user => {
        const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || user.username || '';
        const email = user.email || '';
        const query = userSearchQuery.toLowerCase();
        return fullName.toLowerCase().includes(query) || email.toLowerCase().includes(query);
    });

    const groupedMessages = messageController.groupMessagesByDate(messages);

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                {/* Sidebar */}
                <aside style={{...styles.sidebar, ...(showInfo ? styles.sidebarCollapsed : {})}}>
                    <div style={styles.sidebarHeader}>
                        <h2 style={styles.sidebarTitle}>Messages</h2>
                        <button 
                            style={styles.composeBtn} 
                            onClick={openNewChatModal}
                            title="New Message"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={styles.icon}>
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                        </button>
                    </div>

                    <div style={styles.searchSection}>
                        <div style={styles.searchBox}>
                            <svg style={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search conversations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>
                    </div>

                    <div style={styles.filterTabs}>
                        <button 
                            style={{...styles.filterTab, ...(filter === 'all' ? styles.filterTabActive : {})}}
                            onClick={() => setFilter('all')}
                        >
                            All
                        </button>
                        <button 
                            style={{...styles.filterTab, ...(filter === 'unread' ? styles.filterTabActive : {})}}
                            onClick={() => setFilter('unread')}
                        >
                            Unread
                        </button>
                        <button 
                            style={{...styles.filterTab, ...(filter === 'groups' ? styles.filterTabActive : {})}}
                            onClick={() => setFilter('groups')}
                        >
                            Groups
                        </button>
                    </div>

                    <div style={styles.conversationsList}>
                        {loading ? (
                            <div style={styles.loadingState}>
                                <div style={styles.spinner}></div>
                                <p>Loading...</p>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div style={styles.emptyState}>
                                <p>No conversations</p>
                                <button style={styles.startChatBtn} onClick={openNewChatModal}>
                                    Start New Chat
                                </button>
                            </div>
                        ) : (
                            conversations.map((conv) => (
                                <div
                                    key={conv.id}
                                    style={{
                                        ...styles.conversationItem,
                                        ...(selectedChat?.id === conv.id ? styles.conversationItemActive : {})
                                    }}
                                    onClick={() => setSelectedChat(conv)}
                                >
                                    <div style={styles.avatarWrapper}>
                                        <div style={styles.avatar}>
                                            {conv.displayName?.charAt(0) || '?'}
                                        </div>
                                        {conv.isOnline && <span style={styles.onlineIndicator}></span>}
                                    </div>

                                    <div style={styles.conversationContent}>
                                        <div style={styles.conversationHeader}>
                                            <h4 style={styles.conversationName}>{conv.displayName}</h4>
                                            <span style={styles.conversationTime}>{conv.formattedTime}</span>
                                        </div>
                                        <div style={styles.conversationFooter}>
                                            <p style={styles.lastMessage}>{conv.previewText}</p>
                                            {conv.hasUnread && <span style={styles.unreadBadge}>{conv.unreadCount}</span>}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                {/* Chat Area */}
                <main style={styles.chatArea}>
                    {selectedChat ? (
                        <>
                            <header style={styles.chatHeader}>
                                <div style={styles.chatHeaderInfo}>
                                    <div style={styles.chatAvatar}>
                                        {selectedChat.displayName?.charAt(0) || '?'}
                                    </div>
                                    <div>
                                        <h3 style={styles.chatName}>{selectedChat.displayName}</h3>
                                        <p style={styles.chatStatus}>
                                            {selectedChat.isOnline ? '● Active now' : 'Offline'}
                                        </p>
                                    </div>
                                </div>
                            </header>

                            <div style={styles.messagesContainer}>
                                {Object.entries(groupedMessages).map(([date, msgs]) => (
                                    <React.Fragment key={date}>
                                        <div style={styles.dateDivider}>
                                            <span>{date}</span>
                                        </div>

                                        {msgs.map((msg) => (
                                            <div
                                                key={msg.id}
                                                style={{
                                                    ...styles.message,
                                                    ...(msg.isMine ? styles.messageSent : styles.messageReceived)
                                                }}
                                            >
                                                {!msg.isMine && (
                                                    <div style={styles.messageAvatar}>
                                                        {selectedChat.displayName?.charAt(0)?.toUpperCase() || '?'}
                                                    </div>
                                                )}
                                                
                                                <div style={{
                                                    ...styles.messageContent,
                                                    ...(msg.isMine ? styles.messageContentSent : styles.messageContentReceived)
                                                }}>
                                                    {!msg.isMine && (
                                                        <p style={styles.senderName}>
                                                            {msg.senderName || selectedChat.displayName || 'Unknown'}
                                                        </p>
                                                    )}
                                                    <p style={styles.messageText}>{msg.text}</p>
                                                    <div style={styles.messageFooter}>
                                                        <span style={styles.messageTime}>{msg.formattedTime}</span>
                                                        {msg.isMine && (
                                                            <button 
                                                                style={styles.deleteBtn}
                                                                onClick={() => handleDeleteMessage(msg.id)}
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            <form style={styles.inputArea} onSubmit={sendNewMessage}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileUpload}
                                />
                                
                                <button 
                                    type="button" 
                                    style={styles.actionBtn}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingFile}
                                >
                                    📎
                                </button>

                                <input
                                    type="text"
                                    placeholder="Type your message..."
                                    value={message}
                                    onChange={handleTyping}
                                    style={styles.messageInput}
                                    disabled={sending}
                                />

                                <button 
                                    type="submit" 
                                    style={styles.sendBtn}
                                    disabled={!message.trim() || sending}
                                >
                                    {sending ? '⏳' : '➤'}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div style={styles.emptyChat}>
                            <h2>Select a conversation</h2>
                            <p>Choose from your conversations or start a new one</p>
                            <button style={styles.newChatBtnLarge} onClick={openNewChatModal}>
                                Start New Chat
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {/* New Chat Modal */}
            {showNewChatModal && (
                <div style={styles.modalOverlay} onClick={closeNewChatModal}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>New Conversation</h2>
                            <button style={styles.closeBtn} onClick={closeNewChatModal}>×</button>
                        </div>

                        <div style={styles.modalContent}>
                            {/* Chat Type Toggle */}
                            <div style={styles.chatTypeToggle}>
                                <button
                                    style={{
                                        ...styles.toggleBtn,
                                        ...(isGroupChat ? {} : styles.toggleBtnActive)
                                    }}
                                    onClick={() => {
                                        setIsGroupChat(false);
                                        setSelectedUsers(selectedUsers.slice(0, 1));
                                    }}
                                >
                                    Direct Chat
                                </button>
                                <button
                                    style={{
                                        ...styles.toggleBtn,
                                        ...(isGroupChat ? styles.toggleBtnActive : {})
                                    }}
                                    onClick={() => setIsGroupChat(true)}
                                >
                                    Group Chat
                                </button>
                            </div>

                            {/* Group Name Input */}
                            {isGroupChat && (
                                <div style={styles.groupNameSection}>
                                    <input
                                        type="text"
                                        placeholder="Enter group name..."
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        style={styles.groupNameInput}
                                    />
                                </div>
                            )}

                            {/* Selected Users */}
                            {selectedUsers.length > 0 && (
                                <div style={styles.selectedUsers}>
                                    <p style={styles.selectedLabel}>
                                        Selected ({selectedUsers.length}):
                                    </p>
                                    <div style={styles.selectedUsersList}>
                                        {selectedUsers.map(user => {
                                            const displayName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || user.username || 'Unknown';
                                            return (
                                                <div key={user._id || user.id} style={styles.selectedUserChip}>
                                                    <span>{displayName}</span>
                                                    <button
                                                        style={styles.removeUserBtn}
                                                        onClick={() => toggleUserSelection(user)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* User Search */}
                            <div style={styles.userSearchSection}>
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={userSearchQuery}
                                    onChange={(e) => setUserSearchQuery(e.target.value)}
                                    style={styles.userSearchInput}
                                />
                            </div>

                            {/* Users List */}
                            <div style={styles.usersList}>
                                {loadingUsers ? (
                                    <div style={styles.loadingState}>
                                        <div style={styles.spinner}></div>
                                        <p>Loading users...</p>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div style={styles.emptyState}>
                                        <p>No users found</p>
                                    </div>
                                ) : (
                                    filteredUsers.map(user => {
                                        const isSelected = selectedUsers.some(
                                            u => (u._id || u.id) === (user._id || user.id)
                                        );
                                        const isDisabled = !isGroupChat && selectedUsers.length > 0 && !isSelected;
                                        const displayName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || user.username || 'Unknown User';

                                        return (
                                            <div
                                                key={user._id || user.id}
                                                style={{
                                                    ...styles.userItem,
                                                    ...(isSelected ? styles.userItemSelected : {}),
                                                    ...(isDisabled ? styles.userItemDisabled : {})
                                                }}
                                                onClick={() => !isDisabled && toggleUserSelection(user)}
                                            >
                                                <div style={styles.userAvatar}>
                                                    {displayName.charAt(0).toUpperCase()}
                                                </div>
                                                <div style={styles.userInfo}>
                                                    <h4 style={styles.userName}>{displayName}</h4>
                                                    <p style={styles.userEmail}>{user.email}</p>
                                                </div>
                                                {isSelected && (
                                                    <div style={styles.checkmark}>✓</div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button 
                                style={styles.cancelBtn} 
                                onClick={closeNewChatModal}
                                disabled={creatingConversation}
                            >
                                Cancel
                            </button>
                            <button 
                                style={{
                                    ...styles.createBtn,
                                    ...(selectedUsers.length === 0 || creatingConversation ? styles.createBtnDisabled : {})
                                }}
                                onClick={handleCreateConversation}
                                disabled={selectedUsers.length === 0 || creatingConversation}
                            >
                                {creatingConversation ? 'Creating...' : 
                                 isGroupChat ? `Create Group (${selectedUsers.length})` : 'Start Chat'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    page: {
        width: '100%',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    container: {
        display: 'flex',
        height: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        backgroundColor: '#fff',
        boxShadow: '0 0 20px rgba(0,0,0,0.1)'
    },
    sidebar: {
        width: '350px',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff'
    },
    sidebarCollapsed: {
        width: '80px'
    },
    sidebarHeader: {
        padding: '20px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    sidebarTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '600'
    },
    composeBtn: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: '#007bff',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s'
    },
    icon: {
        width: '20px',
        height: '20px'
    },
    searchSection: {
        padding: '15px 20px'
    },
    searchBox: {
        position: 'relative'
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '20px',
        height: '20px',
        stroke: '#666'
    },
    searchInput: {
        width: '100%',
        padding: '10px 10px 10px 40px',
        border: '1px solid #ddd',
        borderRadius: '20px',
        fontSize: '14px',
        outline: 'none'
    },
    filterTabs: {
        display: 'flex',
        padding: '0 20px',
        gap: '10px',
        borderBottom: '1px solid #e0e0e0'
    },
    filterTab: {
        padding: '10px 15px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#666',
        borderBottom: '2px solid transparent',
        transition: 'all 0.2s'
    },
    filterTabActive: {
        color: '#007bff',
        borderBottomColor: '#007bff',
        fontWeight: '500'
    },
    conversationsList: {
        flex: 1,
        overflowY: 'auto'
    },
    conversationItem: {
        display: 'flex',
        padding: '15px 20px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        borderBottom: '1px solid #f0f0f0'
    },
    conversationItemActive: {
        backgroundColor: '#f0f7ff'
    },
    avatarWrapper: {
        position: 'relative',
        marginRight: '12px'
    },
    avatar: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: '#007bff',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: '600'
    },
    onlineIndicator: {
        position: 'absolute',
        bottom: '2px',
        right: '2px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#4caf50',
        border: '2px solid #fff'
    },
    conversationContent: {
        flex: 1,
        minWidth: 0
    },
    conversationHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '5px'
    },
    conversationName: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '500',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    conversationTime: {
        fontSize: '12px',
        color: '#999',
        flexShrink: 0
    },
    conversationFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    lastMessage: {
        margin: 0,
        fontSize: '14px',
        color: '#666',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1
    },
    unreadBadge: {
        backgroundColor: '#007bff',
        color: '#fff',
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '12px',
        fontWeight: '600',
        minWidth: '20px',
        textAlign: 'center'
    },
    chatArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fafafa'
    },
    chatHeader: {
        padding: '15px 20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fff'
    },
    chatHeaderInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    chatAvatar: {
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        backgroundColor: '#007bff',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: '600'
    },
    chatName: {
        margin: '0 0 5px 0',
        fontSize: '18px',
        fontWeight: '600'
    },
    chatStatus: {
        margin: 0,
        fontSize: '13px',
        color: '#666'
    },
    messagesContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    dateDivider: {
        textAlign: 'center',
        margin: '15px 0',
        fontSize: '12px',
        color: '#999'
    },
    message: {
        display: 'flex',
        marginBottom: '10px',
        alignItems: 'flex-end',
        gap: '8px'
    },
    messageSent: {
        justifyContent: 'flex-end'
    },
    messageReceived: {
        justifyContent: 'flex-start'
    },
    messageAvatar: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#6c757d',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '600',
        flexShrink: 0
    },
    messageContent: {
        maxWidth: '60%',
        borderRadius: '18px',
        padding: '10px 15px',
        position: 'relative'
    },
    messageContentSent: {
        backgroundColor: '#007bff',
        color: '#fff',
        borderBottomRightRadius: '4px'
    },
    messageContentReceived: {
        backgroundColor: '#e9ecef',
        color: '#000',
        borderBottomLeftRadius: '4px'
    },
    senderName: {
        margin: '0 0 4px 0',
        fontSize: '12px',
        fontWeight: '600',
        opacity: 0.8
    },
    messageText: {
        margin: '0 0 5px 0',
        fontSize: '14px',
        wordWrap: 'break-word',
        lineHeight: '1.4'
    },
    messageFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px'
    },
    messageTime: {
        fontSize: '11px',
        opacity: 0.8
    },
    deleteBtn: {
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        fontSize: '20px',
        padding: '0',
        lineHeight: '1',
        opacity: 0.7,
        marginLeft: '5px'
    },
    inputArea: {
        padding: '15px 20px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
    },
    actionBtn: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: '#f0f0f0',
        cursor: 'pointer',
        fontSize: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    messageInput: {
        flex: 1,
        padding: '12px 15px',
        border: '1px solid #ddd',
        borderRadius: '20px',
        fontSize: '14px',
        outline: 'none'
    },
    sendBtn: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: '#007bff',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    emptyChat: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '15px',
        color: '#999'
    },
    newChatBtnLarge: {
        padding: '12px 24px',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500'
    },
    startChatBtn: {
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        marginTop: '10px'
    },
    loadingState: {
        textAlign: 'center',
        padding: '40px',
        color: '#999'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #007bff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 10px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: '#999'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
    },
    modalHeader: {
        padding: '20px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    modalTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '600'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '30px',
        cursor: 'pointer',
        color: '#999',
        lineHeight: '1',
        padding: '0',
        width: '30px',
        height: '30px'
    },
    modalContent: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
    },
    chatTypeToggle: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px'
    },
    toggleBtn: {
        flex: 1,
        padding: '10px',
        border: '2px solid #e0e0e0',
        backgroundColor: '#fff',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s'
    },
    toggleBtnActive: {
        borderColor: '#007bff',
        backgroundColor: '#f0f7ff',
        color: '#007bff'
    },
    groupNameSection: {
        marginBottom: '20px'
    },
    groupNameInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none'
    },
    selectedUsers: {
        marginBottom: '20px'
    },
    selectedLabel: {
        margin: '0 0 10px 0',
        fontSize: '14px',
        fontWeight: '500',
        color: '#666'
    },
    selectedUsersList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    selectedUserChip: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        backgroundColor: '#007bff',
        color: '#fff',
        borderRadius: '20px',
        fontSize: '14px'
    },
    removeUserBtn: {
        background: 'none',
        border: 'none',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '18px',
        lineHeight: '1',
        padding: '0'
    },
    userSearchSection: {
        marginBottom: '15px'
    },
    userSearchInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none'
    },
    usersList: {
        maxHeight: '300px',
        overflowY: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: '8px'
    },
    userItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        transition: 'background-color 0.2s'
    },
    userItemSelected: {
        backgroundColor: '#f0f7ff'
    },
    userItemDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed'
    },
    userAvatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#007bff',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: '600',
        marginRight: '12px'
    },
    userInfo: {
        flex: 1
    },
    userName: {
        margin: '0 0 4px 0',
        fontSize: '15px',
        fontWeight: '500'
    },
    userEmail: {
        margin: 0,
        fontSize: '13px',
        color: '#666'
    },
    checkmark: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#4caf50',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: '600'
    },
    modalFooter: {
        padding: '15px 20px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px'
    },
    cancelBtn: {
        padding: '10px 20px',
        border: '1px solid #ddd',
        backgroundColor: '#fff',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    },
    createBtn: {
        padding: '10px 20px',
        border: 'none',
        backgroundColor: '#007bff',
        color: '#fff',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    },
    createBtnDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed'
    }
};

export default MessagesPage;